<template>
  <div class="page-wrap">
    <PageHeader
      title="사용 현황"
    />

    <!-- 분류 탭 -->
    <v-tabs
      v-model="category"
      color="primary"
      density="comfortable"
      class="level-tabs"
    >
      <v-tab value="paid">유료</v-tab>
      <v-tab value="free">무료</v-tab>
      <v-tab value="ai">AI미래교육연구회</v-tab>
      <v-tab value="expo">박람회</v-tab>
    </v-tabs>

    <!-- 요약 KPI 카드 -->
    <section class="stat-grid" aria-label="접속 현황 요약">
      <div class="stat-card">
        <span class="stat-card__icon stat-card__icon--primary">
          <v-icon icon="mdi-school-outline" size="18" />
        </span>
        <div class="stat-card__body">
          <span class="stat-card__label">학교 수</span>
          <span class="stat-card__count">{{ summary.schoolCount.toLocaleString() }}</span>
        </div>
      </div>
      <div class="stat-card">
        <span class="stat-card__icon stat-card__icon--info">
          <v-icon icon="mdi-account-multiple-outline" size="18" />
        </span>
        <div class="stat-card__body">
          <span class="stat-card__label">발급 계정</span>
          <span class="stat-card__count">{{ summary.totalCount.toLocaleString() }}</span>
        </div>
      </div>
      <div class="stat-card">
        <span class="stat-card__icon stat-card__icon--success">
          <v-icon icon="mdi-login-variant" size="18" />
        </span>
        <div class="stat-card__body">
          <span class="stat-card__label">접속 계정</span>
          <span class="stat-card__count">{{ summary.loginCount.toLocaleString() }}</span>
        </div>
      </div>
      <div class="stat-card">
        <span class="stat-card__icon stat-card__icon--rate">
          <v-icon icon="mdi-chart-donut" size="18" />
        </span>
        <div class="stat-card__body">
          <span class="stat-card__label">전체 접속률</span>
          <span class="stat-card__count">
            {{ summary.loginRate }}<em class="stat-card__percent">%</em>
          </span>
        </div>
      </div>
    </section>

    <!-- 학교별 접속 현황 테이블 -->
    <DataTable
      class="usage-table"
      :headers="headers"
      :items="filteredSchools"
      :loading="loading"
      row-key="schoolCode"
      clickable
      @row-click="openDetail"
    >
      <template #toolbar>
        <div class="filter-bar">
          <v-text-field
            v-model="search"
            placeholder="학교명 / 학교코드 검색"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            variant="outlined"
            hide-details
            clearable
            class="search-field"
          />

          <!-- 접속 상태 필터 -->
          <v-btn-toggle
            v-model="loginFilter"
            mandatory
            density="comfortable"
            color="primary"
            variant="outlined"
            divided
            class="login-filter-toggle"
          >
            <v-btn value="all" size="small">전체</v-btn>
            <v-btn value="active" size="small">접속 학교</v-btn>
            <v-btn value="inactive" size="small">미접속 학교</v-btn>
          </v-btn-toggle>

          <!-- 접속률 구간 필터 -->
          <v-select
            v-model="rateFilter"
            :items="rateFilterItems"
            density="compact"
            variant="outlined"
            hide-details
            class="rate-select"
          >
            <template #selection="{ item }">
              <span class="rate-select__selection">
                <span
                  v-if="item.raw.dot"
                  class="rate-select__dot"
                  :class="`rate-select__dot--${item.raw.dot}`"
                />
                {{ item.title }}
              </span>
            </template>
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps">
                <template #prepend>
                  <span
                    class="rate-select__dot"
                    :class="item.raw.dot ? `rate-select__dot--${item.raw.dot}` : 'rate-select__dot--none'"
                  />
                </template>
              </v-list-item>
            </template>
          </v-select>

          <!-- 생성일 정렬 -->
          <v-select
            v-model="sortOrder"
            :items="sortOrderItems"
            density="compact"
            variant="outlined"
            hide-details
            prepend-inner-icon="mdi-sort-calendar-descending"
            class="sort-select"
          />

          <v-checkbox
            v-model="hideNoAccount"
            label="미발급 학교 제외"
            density="compact"
            hide-details
            class="no-account-filter"
          />

          <v-btn
            v-if="isFiltered"
            variant="text"
            color="primary"
            size="small"
            prepend-icon="mdi-filter-off-outline"
            @click="resetFilters"
          >
            필터 초기화
          </v-btn>

          <v-spacer />

          <span class="result-count">
            <b>{{ filteredSchools.length }}</b> / {{ schools.length }}개 학교
          </span>
          <v-btn
            variant="outlined"
            color="primary"
            size="small"
            :loading="loading"
            @click="fetchList"
          >
            새로고침
          </v-btn>
        </div>
      </template>

      <template #[`item.schoolName`]="{ item }">
        <span class="school-name" :title="item.schoolName">{{ item.schoolName }}</span>
      </template>

      <template #[`item.schoolCode`]="{ item }">
        <span class="code-chip">{{ item.schoolCode }}</span>
      </template>

      <template #[`item.createdAt`]="{ item }">
        <span :class="item.createdAt ? 'date-text' : 'empty-text'">
          {{ formatDate(item.createdAt) }}
        </span>
      </template>

      <template #[`item.loginRate`]="{ item }">
        <div class="rate-cell">
          <v-progress-linear
            :model-value="item.loginRate"
            :color="rateColor(item.loginRate)"
            height="6"
            rounded
            class="rate-bar"
          />
          <span class="rate-text">{{ item.loginRate }}%</span>
        </div>
      </template>

      <template #[`item.studentLogin`]="{ item }">
        <span class="count-pair">
          <b>{{ item.studentLoginCount }}</b>
          <em>/ {{ item.studentCount }}</em>
        </span>
      </template>

      <template #[`item.teacherLogin`]="{ item }">
        <span class="count-pair">
          <b>{{ item.teacherLoginCount }}</b>
          <em>/ {{ item.teacherCount }}</em>
        </span>
      </template>

      <template #[`item.lastLogin`]="{ item }">
        <span :class="item.lastLogin ? 'date-text' : 'empty-text'">
          {{ item.lastLogin || "-" }}
        </span>
      </template>

      <template #empty>
        <EmptyState
          title="데이터가 없습니다"
          description="조건에 해당하는 학교가 없습니다."
        />
      </template>
    </DataTable>

    <!-- 학교 상세 다이얼로그 — 계정별 접속 현황 -->
    <!-- max-width 1080: 계정 상세 표(약 950px)가 가로 스크롤 없이 들어가는 폭 -->
    <v-dialog v-model="detailDialog" max-width="1080" scrollable>
      <v-card class="detail-card">
        <v-card-title class="detail-title">
          <div>
            <span class="detail-school-name">{{ detailSchool?.schoolName }}</span>
            <span class="detail-school-code">({{ detailSchool?.schoolCode }})</span>
          </div>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="detailDialog = false"
          />
        </v-card-title>

        <v-card-text>
          <div class="detail-summary" v-if="detail">
            <span class="detail-chip">
              발급 <b>{{ detail.totalCount }}</b>
            </span>
            <span class="detail-chip">
              접속 <b>{{ detail.loginCount }}</b>
            </span>
            <span class="detail-chip">
              접속률 <b>{{ detail.loginRate }}%</b>
            </span>
          </div>

          <DataTable
            class="usage-table detail-table"
            :headers="detailHeaders"
            :items="detail?.users || []"
            :loading="detailLoading"
            row-key="id"
            density="compact"
            :items-per-page="50"
          >
            <template #[`item.classInfo`]="{ item }">
              <span class="date-text">
                {{ item.grade }}-{{ item.classNum }}-{{ String(item.attendanceNumber).padStart(2, "0") }}
              </span>
            </template>

            <template #[`item.schoolLevel`]="{ item }">
              {{ levelLabel(item.schoolLevel) }}
            </template>

            <template #[`item.hasLoggedIn`]="{ item }">
              <span
                class="login-badge"
                :class="item.hasLoggedIn ? 'login-badge--yes' : 'login-badge--no'"
              >
                {{ item.hasLoggedIn ? "접속" : "미접속" }}
              </span>
            </template>

            <!-- 폭 절약을 위해 날짜만 표시 — 전체 시각은 마우스 오버 툴팁 -->
            <template #[`item.lastLogin`]="{ item }">
              <span
                :class="item.lastLogin ? 'date-text' : 'empty-text'"
                :title="item.lastLogin || ''"
              >
                {{ item.lastLogin ? formatDate(item.lastLogin) : "-" }}
              </span>
            </template>
          </DataTable>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getloginstatus, getloginstatusdetail, getschoollist } from "@/api/user-api-list";
import { getSchoolCreate } from "@/api/guest-api-list";
import { resolveCategory } from "@/constants/schoolType";
import PageHeader from "@/components/ui/PageHeader.vue";
import DataTable from "@/components/ui/DataTable.vue";
import EmptyState from "@/components/ui/EmptyState.vue";

export default {
  name: "UsageStatusView",
  components: { PageHeader, DataTable, EmptyState },
  data() {
    return {
      category: "paid", // paid | free | ai | expo
      schools: [],
      loading: false,
      search: "",
      loginFilter: "all", // all | active(접속 학교) | inactive(미접속 학교)
      rateFilter: "all", // all | high(70%↑) | mid(30~70%) | low(30%↓)
      hideNoAccount: false, // 계정 미발급(totalCount 0) 학교 제외
      rateFilterItems: [
        { title: "접속률 전체", value: "all", dot: null },
        { title: "70% 이상", value: "high", dot: "high" },
        { title: "30% ~ 70%", value: "mid", dot: "mid" },
        { title: "30% 미만", value: "low", dot: "low" },
      ],
      sortOrder: "newest", // newest(최신 생성순) | oldest(과거 생성순)
      sortOrderItems: [
        { title: "최신 생성순", value: "newest" },
        { title: "과거 생성순", value: "oldest" },
      ],

      detailDialog: false,
      detailLoading: false,
      detailSchool: null,
      detail: null,

      headers: [
        { title: "학교명", key: "schoolName", align: "start", width: 200, minWidth: 150 },
        { title: "코드", key: "schoolCode", align: "center", width: 90 },
        { title: "생성일", key: "createdAt", align: "center", width: 110 },
        { title: "발급 계정", key: "totalCount", align: "center", width: 100 },
        { title: "접속 계정", key: "loginCount", align: "center", width: 100 },
        { title: "접속률", key: "loginRate", align: "center", width: 200 },
        { title: "학생 접속", key: "studentLogin", sortable: false, align: "center", width: 110 },
        { title: "교사 접속", key: "teacherLogin", sortable: false, align: "center", width: 110 },
        { title: "최근 접속일", key: "lastLogin", align: "center", width: 130 },
      ],
      detailHeaders: [
        { title: "ID", key: "id", align: "start", minWidth: 110 },
        { title: "이름", key: "name", align: "start", minWidth: 70 },
        { title: "학년-반-번호", key: "classInfo", align: "center", width: 100 },
        { title: "구분", key: "schoolLevel", align: "center", width: 64 },
        { title: "접속", key: "hasLoggedIn", align: "center", width: 76 },
        { title: "접속일수", key: "loginDaysCount", align: "center", width: 74 },
        { title: "최근 접속일", key: "lastLogin", align: "center", width: 104 },
      ],
    };
  },
  computed: {
    filteredSchools() {
      let list = this.schools;

      // 계정 미발급 학교 제외
      if (this.hideNoAccount) {
        list = list.filter((s) => s.totalCount > 0);
      }

      // 접속 상태 — 접속: 1명 이상 접속 / 미접속: 발급됐지만 아무도 접속 안 함
      if (this.loginFilter === "active") {
        list = list.filter((s) => s.loginCount > 0);
      } else if (this.loginFilter === "inactive") {
        list = list.filter((s) => s.totalCount > 0 && s.loginCount === 0);
      }

      // 접속률 구간 — 테이블 접속률 바 색상 기준과 동일 (70 / 30)
      if (this.rateFilter === "high") {
        list = list.filter((s) => s.totalCount > 0 && s.loginRate >= 70);
      } else if (this.rateFilter === "mid") {
        list = list.filter(
          (s) => s.totalCount > 0 && s.loginRate >= 30 && s.loginRate < 70
        );
      } else if (this.rateFilter === "low") {
        list = list.filter((s) => s.totalCount > 0 && s.loginRate < 30);
      }

      const q = (this.search || "").trim().toLowerCase();
      if (q) {
        list = list.filter(
          (s) =>
            (s.schoolName || "").toLowerCase().includes(q) ||
            (s.schoolCode || "").toLowerCase().includes(q)
        );
      }

      // 생성일 정렬 — 기본 최신 생성순. createdAt 없는 학교는 가장 과거로 취급
      const time = (s) => (s.createdAt ? new Date(s.createdAt).getTime() : 0);
      return [...list].sort((a, b) =>
        this.sortOrder === "oldest" ? time(a) - time(b) : time(b) - time(a)
      );
    },
    isFiltered() {
      return (
        this.loginFilter !== "all" ||
        this.rateFilter !== "all" ||
        this.hideNoAccount ||
        !!(this.search || "").trim()
      );
    },
    summary() {
      const totalCount = this.schools.reduce((sum, s) => sum + s.totalCount, 0);
      const loginCount = this.schools.reduce((sum, s) => sum + s.loginCount, 0);
      return {
        schoolCount: this.schools.length,
        totalCount,
        loginCount,
        loginRate:
          totalCount > 0 ? Math.round((loginCount / totalCount) * 1000) / 10 : 0,
      };
    },
  },
  async created() {
    await this.fetchList();
  },
  watch: {
    async category() {
      await this.fetchList();
    },
  },
  methods: {
    async fetchList() {
      this.loading = true;
      try {
        // AI미래교육연구회/박람회/연수/서포터즈는 schoolLevel(유료/체험)과 무관하게
        // school.type으로만 정해진다 — "학교 조회" 탭과 동일한 기준(resolveCategory)을
        // 그대로 적용하기 위해, 유료/체험 학교 목록을 둘 다 받아서 분류부터 판정한다.
        const [paidListRes, guestListRes] = await Promise.all([
          getschoollist(),
          getSchoolCreate(),
        ]);
        const paidSchools = (paidListRes?.data?.data ?? []).map((s) => ({
          ...s,
          schoolLevel: s.schoolLevel ?? "user",
        }));
        const guestSchools = (guestListRes?.data?.data ?? []).map((s) => ({
          ...s,
          schoolLevel: s.schoolLevel ?? "guest",
        }));
        const codesInCategory = new Set(
          [...paidSchools, ...guestSchools]
            .filter((s) => resolveCategory(s) === this.category)
            .map((s) => s.schoolCode)
        );

        // 접속 현황 API는 schoolLevel별로만 조회되므로 유료/체험 둘 다 받아서 합친 뒤,
        // 위에서 판정한 분류 기준으로 걸러낸다.
        const [userLoginRes, guestLoginRes] = await Promise.all([
          getloginstatus("user"),
          getloginstatus("guest"),
        ]);
        const userLogin = Array.isArray(userLoginRes?.data?.data) ? userLoginRes.data.data : [];
        const guestLogin = Array.isArray(guestLoginRes?.data?.data) ? guestLoginRes.data.data : [];

        this.schools = [...userLogin, ...guestLogin].filter((s) =>
          codesInCategory.has(s.schoolCode)
        );
      } finally {
        this.loading = false;
      }
    },
    async openDetail(school) {
      this.detailSchool = school;
      this.detail = null;
      this.detailDialog = true;
      this.detailLoading = true;
      try {
        const res = await getloginstatusdetail(school.schoolCode);
        this.detail = res?.data?.data || null;
      } finally {
        this.detailLoading = false;
      }
    },
    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "-";
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${d.getFullYear()}-${mm}-${dd}`;
    },
    resetFilters() {
      this.loginFilter = "all";
      this.rateFilter = "all";
      this.hideNoAccount = false;
      this.search = "";
    },
    rateColor(rate) {
      if (rate >= 70) return "success";
      if (rate >= 30) return "warning";
      return "error";
    },
    levelLabel(level) {
      if (level === "student") return "학생";
      if (level === "teacher") return "교사";
      if (level === "lms") return "LMS";
      return level;
    },
  },
};
</script>

<style scoped>
.page-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.level-tabs {
  border-bottom: 1px solid var(--border-default);
}
.level-tabs :deep(.v-tab) {
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

/* ── 요약 KPI 카드 ── */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-3, 12px);
}
@media (max-width: 720px) {
  .stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.stat-card {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  transition: box-shadow 0.15s ease;
}
.stat-card:hover {
  box-shadow: var(--shadow-md, 0 2px 8px rgba(0, 0, 0, 0.08));
}

.stat-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  flex-shrink: 0;
}
.stat-card__icon--primary {
  background: var(--color-primary-50);
  color: var(--color-primary-600);
}
.stat-card__icon--info {
  background: var(--color-info-bg);
  color: var(--color-info-text);
}
.stat-card__icon--success {
  background: var(--color-success-bg);
  color: var(--color-success-text);
}
.stat-card__icon--rate {
  background: var(--color-warning-bg);
  color: var(--color-warning-text);
}

.stat-card__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.stat-card__label {
  font-size: var(--font-caption-size);
  color: var(--text-secondary);
  font-weight: 600;
  white-space: nowrap;
}
.stat-card__count {
  font-size: 24px;
  line-height: 1.15;
  font-weight: 700;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}
.stat-card__percent {
  font-style: normal;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-tertiary);
  margin-left: 1px;
}

/* ── 테이블 툴바 (필터 바) ── */
.filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  width: 100%;
}
.search-field {
  flex: 0 1 240px;
  min-width: 180px;
}

.login-filter-toggle {
  flex: 0 0 auto;
  height: 36px !important;
}
.login-filter-toggle :deep(.v-btn) {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.rate-select {
  flex: 0 0 150px;
}
.rate-select__selection {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  white-space: nowrap;
}
.rate-select__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.rate-select__dot--high {
  background: var(--color-success-text);
}
.rate-select__dot--mid {
  background: var(--color-warning-text);
}
.rate-select__dot--low {
  background: var(--color-error-text);
}
.rate-select__dot--none {
  background: var(--border-default);
}

.sort-select {
  flex: 0 0 170px;
}
.sort-select :deep(.v-field__input) {
  font-size: 13px;
}

.no-account-filter {
  flex: 0 0 auto;
}
.no-account-filter :deep(.v-label) {
  font-size: 13px;
  opacity: 1;
  color: var(--text-secondary);
}

.result-count {
  font-size: var(--font-caption-size);
  color: var(--text-tertiary);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.result-count b {
  color: var(--text-primary);
}

/* ── 테이블 공통 — 줄바꿈 방지 + 세로 중앙 정렬 ──
   .usage-table 클래스 기준으로 지정 — v-dialog로 teleport된 상세 테이블에도 적용됨 */
.usage-table :deep(th),
.usage-table :deep(td) {
  white-space: nowrap;
  vertical-align: middle;
}
.usage-table :deep(th .v-data-table-header__content) {
  white-space: nowrap;
}

/* ── 가로 스크롤 — 창이 좁아지면 표가 잘리는 대신 스크롤되도록, 스크롤바는 항상 노출 ── */
.usage-table :deep(.v-table__wrapper) {
  overflow-x: auto;
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: var(--border-default) transparent;
}
.usage-table :deep(.v-table__wrapper)::-webkit-scrollbar {
  height: 8px;
}
.usage-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb {
  background: var(--border-default);
  border-radius: 4px;
}
.usage-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb:hover {
  background: var(--text-tertiary);
}
.usage-table :deep(.v-table__wrapper)::-webkit-scrollbar-track {
  background: transparent;
}

/* ── 첫 컬럼(학교명/ID) 고정 — 가로 스크롤 중에도 행 식별 유지 ── */
.usage-table :deep(thead th:first-child),
.usage-table :deep(tbody td:first-child) {
  position: sticky !important; /* Vuetify가 td에 position: relative를 걸어 override 필요 */
  left: 0;
  z-index: 2;
}
.usage-table :deep(thead th:first-child) {
  z-index: 3; /* sticky header(top)와 교차 지점에서 위에 오도록 */
}
/* 스크롤 시작 시 고정 컬럼 경계 표시 */
.usage-table :deep(thead th:first-child),
.usage-table :deep(tbody td:first-child) {
  border-right: 1px solid var(--border-default);
  box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.08);
}

/* ── 학교명 / 코드 셀 ── */
.school-name {
  font-weight: 600;
  color: var(--text-primary);
  /* 학교명 컬럼이 남는 공간을 독식하지 않도록 고정 폭 + 말줄임 (전체 이름은 툴팁) */
  display: inline-block;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.code-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: var(--radius-sm);
  background: var(--bg-subtle);
  border: 1px solid var(--border-default);
  font-size: var(--font-caption-size);
  color: var(--text-secondary);
  font-family: monospace;
  letter-spacing: 0.03em;
}

/* ── 접속/발급 카운트 셀 ── */
.count-pair {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  font-variant-numeric: tabular-nums;
}
.count-pair b {
  color: var(--text-primary);
  font-weight: 700;
}
.count-pair em {
  font-style: normal;
  color: var(--text-tertiary);
  font-size: var(--font-caption-size);
}

/* ── 날짜 / 빈 값 셀 ── */
.date-text {
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
}
.empty-text {
  color: var(--text-tertiary);
}

/* ── 접속률 셀 ── */
.rate-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
}
/* v-progress-linear는 기본적으로 left:50% + translateX(-50%)로 스스로를 가운데 배치함
   — flex 안에서는 이 포지셔닝이 바를 옆으로 밀어 텍스트와 겹치므로 무효화 */
.rate-bar {
  flex: 0 0 100px;
  width: 100px;
  left: 0 !important;
  transform: none !important;
}
.rate-text {
  flex: 0 0 44px;
  font-size: var(--font-caption-size);
  font-weight: 600;
  color: var(--text-secondary);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* ── 상세 다이얼로그 ── */
.detail-card {
  border-radius: var(--radius-md);
}
/* 상세 테이블 — 좁은 팝업에서도 가로 스크롤 없이 들어가도록 셀 패딩 축소 */
.detail-table :deep(th),
.detail-table :deep(td) {
  padding: 0 10px !important;
}
.detail-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5) !important;
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--border-default);
}
.detail-school-name {
  font-weight: 700;
  color: var(--text-primary);
}
.detail-school-code {
  color: var(--text-tertiary);
  font-size: var(--font-body-size);
  margin-left: 6px;
  font-family: monospace;
}
.detail-summary {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: var(--space-4) 0 var(--space-3);
}
.detail-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: var(--radius-pill);
  background: var(--bg-subtle);
  border: 1px solid var(--border-default);
  font-size: var(--font-caption-size);
  color: var(--text-secondary);
}
.detail-chip b {
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

/* ── 접속 여부 배지 ── */
.login-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  font-size: var(--font-caption-size);
  line-height: 1;
  font-weight: 600;
  white-space: nowrap;
}
.login-badge--yes {
  background: var(--color-success-bg);
  color: var(--color-success-text);
  border: 1px solid var(--color-success-border);
}
.login-badge--no {
  background: var(--bg-muted, #f1f2f4);
  color: var(--text-tertiary);
  border: 1px solid var(--border-default);
}
</style>
